import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Cache for MMD locations to avoid repeated API calls
let cachedLocations = null;
let cachedDetailedLocations = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// All available grades (used as fallback)
const ALL_GRADES = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];

/**
 * Hook to fetch MMD locations from the API
 * @param {boolean} forceRefresh - Force refresh from API even if cached
 * @param {boolean} useFullName - Return full names like "Kolkata MMD" instead of just "Kolkata"
 * @returns {Object} { locations, loading, error, refetch }
 */
export const useMMDLocations = (forceRefresh = false, useFullName = false) => {
  const [locations, setLocations] = useState(cachedLocations || []);
  const [loading, setLoading] = useState(!cachedLocations);
  const [error, setError] = useState(null);

  const fetchLocations = async () => {
    // Check cache first
    if (!forceRefresh && cachedLocations && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      const data = useFullName 
        ? cachedDetailedLocations?.map(loc => loc.name) || cachedLocations.map(c => `${c} MMD`)
        : cachedLocations;
      setLocations(data);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/mmd/locations/public`);
      const cityData = response.data.map(loc => loc.city); // Array of city names
      const fullNameData = response.data.map(loc => loc.name); // Array of full names like "Kolkata MMD"
      
      // Update cache
      cachedLocations = cityData;
      cachedDetailedLocations = response.data; // Cache detailed data too
      cacheTimestamp = Date.now();
      
      setLocations(useFullName ? fullNameData : cityData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch MMD locations:', err);
      setError(err);
      // Fallback to expanded locations list (matches API seed data)
      const fallbackCities = ['Mumbai', 'Chennai', 'Kolkata', 'Kochi', 'Noida', 'Visakhapatnam', 'Goa', 'Kandla'];
      const fallback = useFullName ? fallbackCities.map(c => `${c} MMD`) : fallbackCities;
      setLocations(fallback);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, [forceRefresh, useFullName]);

  return { 
    locations, 
    loading, 
    error, 
    refetch: () => fetchLocations() 
  };
};

/**
 * Hook to fetch full MMD location objects from the API
 * @returns {Object} { locations, loading, error, refetch, getGradesForMMD }
 */
export const useMMDLocationsDetailed = () => {
  const [locations, setLocations] = useState(cachedDetailedLocations || []);
  const [loading, setLoading] = useState(!cachedDetailedLocations);
  const [error, setError] = useState(null);

  const fetchLocations = async () => {
    // Check cache first
    if (cachedDetailedLocations && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      setLocations(cachedDetailedLocations);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/mmd/locations/public`);
      cachedDetailedLocations = response.data;
      cacheTimestamp = Date.now();
      setLocations(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch MMD locations:', err);
      setError(err);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  // Get supported grades for a specific MMD
  const getGradesForMMD = useCallback((mmdCity) => {
    if (!mmdCity) return ALL_GRADES;
    const mmd = locations.find(loc => loc.city === mmdCity);
    if (!mmd || !mmd.grades || mmd.grades.length === 0) return ALL_GRADES;
    return mmd.grades;
  }, [locations]);

  // Get MMDs that support a specific grade
  const getMMDsForGrade = useCallback((grade) => {
    if (!grade) return locations.map(loc => loc.city);
    return locations
      .filter(loc => !loc.grades || loc.grades.length === 0 || loc.grades.includes(grade))
      .map(loc => loc.city);
  }, [locations]);

  useEffect(() => {
    fetchLocations();
  }, []);

  return { 
    locations, 
    loading, 
    error, 
    refetch: fetchLocations,
    getGradesForMMD,
    getMMDsForGrade,
    allGrades: ALL_GRADES
  };
};

/**
 * Hook to get grades filtered by selected MMD
 * @param {string} selectedMMD - The selected MMD city name
 * @returns {Object} { grades, loading, allGrades }
 */
export const useGradesForMMD = (selectedMMD) => {
  const { locations, loading, getGradesForMMD, allGrades } = useMMDLocationsDetailed();
  
  // Use useMemo to derive grades without causing cascading renders
  const grades = React.useMemo(() => {
    if (!loading && locations.length > 0 && selectedMMD) {
      return getGradesForMMD(selectedMMD);
    }
    return allGrades;
  }, [selectedMMD, loading, locations, getGradesForMMD, allGrades]);

  return { 
    grades, 
    loading,
    allGrades
  };
};

/**
 * Clear the MMD locations cache (call after admin updates MMD list)
 */
export const clearMMDCache = () => {
  cachedLocations = null;
  cachedDetailedLocations = null;
  cacheTimestamp = null;
};

export default useMMDLocations;
